import React, { SyntheticEvent, useState, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import MBImage from 'components/components/MBImage';
import { pxToEm } from 'theme';
import palette from 'theme/palette';
import { AdvancedInfo } from './AdvancedInfo';
import { AdvancedInfoHover } from './AdvancedInfoHover';
import { BottomProductInfo } from './BottomProductInfo';
import { CardInfo } from './CardInfo';
import { CardSelect } from './CardSelect';
import { ColorVariants } from './ColorVariants';
import { FavoritesButtons } from './FavoritesButtons';
import * as Styles from './ProductCard.styles';
import { ProductCardProps } from './ProductCard.types';
import { TopProductInfo } from './TopProductInfo';

const ProductCard = ({
  imageUrl,
  features = [],
  title,
  blurhash,
  topInfoItems = [],
  isSelected,
  imageDecodeMode = 'eager',
  aspectRatio = 'original',
  showSelect = false,
  showSelectOnHover = false,
  showHoverFeatures = false,
  showFeatures = false,
  isAddToCartLoading = false,
  disabled = false,
  isAddedToCart = false,
  isAddToFavoritesLoading = false,
  shouldAlwaysShowFavoritesButton = false,
  isOvernight = false,
  showBag = true,
  selectedProject = null,
  projects = [],
  actionsButtons,
  entityId = undefined,
  productUrl,
  childrenNumber,
  isHoverAllowed = true,
  isOutlined = false,
  showProjectsDropdown = true,
  isMobileDevice = false,
  isSmallDesktop = false,
  showAdvancedInfo = false,
  centerAdvancedInfo = true,
  subtitle1,
  subtitle2,
  categoryName,
  brandName,
  actionType = 'addToCart',
  actionButtonText,
  hideActionButton,
  addToCartLabel = 'Add to Cart',
  addToCartHoverLabel,
  addToCartTooltipLabel,
  addedToCartLabel = 'Added to Cart',
  createProjectButtonLabel = 'Create Project',
  inStockLabel = 'Ships overnight',
  outOfStockLabel,
  projectPreLabel = 'Project: ',
  saveButtonLabel = 'Add to Board',
  isCompactDescription,
  showOnHoverOnlyOvernight = false,
  searchPlaceholderLabel = 'Search',
  isDigital = false,
  withColorVariants = false,
  colorVariantsNumber = 0,
  colorVariants = [],
  tooltipTitle,
  activeVariantId,
  isSelectboxDisabled = false,
  checkboxTooltipTitle = undefined,
  shouldHideFavoritesButton = false,
  onProductClick,
  onRightClick,
  onClickAddToFavorites,
  onActionButtonClick: propOnActionButtonClick,
  onSelected,
  onAddToBag,
  onProjectClick,
  onCreateProject,
  onVariantClick,
  customTopRightButtonCallback,
}: ProductCardProps) => {
  const [isCardHover, setIsHover] = useState(false);
  const [shouldShowContainedDropdown, setShouldShowContainedDropdown] =
    useState(false);
  const isHover = isCardHover && isHoverAllowed;
  const shouldShowEllipsis =
    actionsButtons != null && ((isHover && !isMobileDevice) || isMobileDevice);
  const hasProjects = projects.length > 0;

  const handleMouseMove = () => {
    if (!isHover) {
      setIsHover(true);
    }
  };

  const handleSelectClicked: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    e.stopPropagation();
    onSelected?.(!isSelected);
  };

  const handleCoverImageClick = (e: SyntheticEvent) => {
    e.preventDefault();
    if (showSelect) {
      onSelected?.();
    } else {
      onProductClick?.();
    }
  };

  const handleProjectClick = (id: number) => {
    onProjectClick?.(id);
    setShouldShowContainedDropdown(false);
    setIsHover(false);
  };

  const handleRightClick = () => {
    onRightClick?.();
  };

  const shouldShowDetailedInfo = showAdvancedInfo;
  const isFavoritesCallback =
    customTopRightButtonCallback != null || onClickAddToFavorites != null;
  const showAddToFavorites =
    ((isFavoritesCallback && isHover) ||
      (!shouldShowDetailedInfo && shouldShowContainedDropdown)) &&
    !showSelect;
  const shouldShowSelect =
    showSelect || isSelected || (showSelectOnHover && isHover);
  const shouldShowFeatures =
    (showHoverFeatures && !showSelect && isHover) || showFeatures;

  const shouldShowBag = showBag && !showSelect;
  const showInfoDropdown =
    hasProjects &&
    selectedProject != null &&
    shouldShowBag &&
    !isAddedToCart &&
    showProjectsDropdown;
  const showTopProductInfo = !isSelected && !showSelect && !isHover;

  const isCustomAction = actionType === 'customAction';

  const shouldShowHoverAdvancedInfo =
    showAdvancedInfo &&
    (actionType === 'addToCart' || isCustomAction) &&
    showProjectsDropdown &&
    hasProjects &&
    selectedProject != null &&
    shouldShowBag &&
    isHover &&
    !isAddedToCart;

  const actionButtonLabel = useMemo(() => {
    if (actionType === 'link' || isCustomAction) {
      return actionButtonText;
    } else if (isAddedToCart) {
      return addedToCartLabel;
    } else {
      return addToCartLabel;
    }
  }, [
    actionType,
    isCustomAction,
    isAddedToCart,
    actionButtonText,
    addedToCartLabel,
    addToCartLabel,
  ]);

  const onActionButtonClick = useCallback(() => {
    if (isAddToCartLoading) {
      return;
    }

    if (actionType === 'addToCart' && !isAddedToCart) {
      onAddToBag?.(selectedProject?.id);
    }

    if (isCustomAction) {
      propOnActionButtonClick?.();
    }
  }, [
    isAddToCartLoading,
    actionType,
    isAddedToCart,
    isCustomAction,
    onAddToBag,
    selectedProject?.id,
    propOnActionButtonClick,
  ]);

  return (
    <Styles.RelativeContainer
      disabled={disabled}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onMouseMove={handleMouseMove}
    >
      <Styles.TooltipCursor
        followCursor
        PopperProps={{ disablePortal: true }}
        title={tooltipTitle}
      >
        <Styles.Container
          disabled={disabled}
          showSelect={showSelect}
          isAddToCartLoading={isAddToCartLoading}
        >
          {shouldShowSelect ? (
            <CardSelect
              isSelected={!!isSelected}
              isSelectboxDisabled={isSelectboxDisabled}
              showAdvancedInfo={showAdvancedInfo}
              checkboxTooltipTitle={checkboxTooltipTitle}
              handleSelectClicked={handleSelectClicked}
            />
          ) : null}
          {shouldShowFeatures && (
            <BottomProductInfo
              isHover={isHover}
              showInfoDropdown={showInfoDropdown}
              shouldShowDetailedInfo={shouldShowDetailedInfo}
              features={features}
              handleBottomInfoClick={handleCoverImageClick}
            />
          )}
          <Styles.CoverContainer
            isMobile={isMobileDevice}
            isHover={isHover}
            isOutlined={isOutlined}
            onContextMenu={handleRightClick}
          >
            {shouldShowEllipsis && actionsButtons}
            {showTopProductInfo && (
              <Styles.TopWrapper>
                <TopProductInfo topInfoItems={topInfoItems} />
              </Styles.TopWrapper>
            )}
            {!shouldHideFavoritesButton && (
              <FavoritesButtons
                entityId={entityId}
                isFavoritesCallback={isFavoritesCallback}
                onClickAddToFavorites={
                  customTopRightButtonCallback != null
                    ? customTopRightButtonCallback
                    : onClickAddToFavorites
                }
                shouldAlwaysShowFavoritesButton={
                  shouldAlwaysShowFavoritesButton
                }
                showAddToFavorites={showAddToFavorites}
                isAddToFavoritesLoading={isAddToFavoritesLoading}
                right={shouldShowEllipsis ? 48 : 16}
                isMobileDevice={isMobileDevice}
                saveButtonLabel={saveButtonLabel}
              />
            )}

            <MBImage
              url={productUrl}
              imageUrl={imageUrl}
              onClick={handleCoverImageClick}
              aspectRatio={aspectRatio}
              blurHash={blurhash}
              imageDecodeMode={imageDecodeMode}
              alt={title}
              bgColor={palette.grey.overlay}
            />
            {(isHover ||
              isSelected ||
              (!shouldShowDetailedInfo && shouldShowContainedDropdown)) && (
              <Styles.DimmedBackground />
            )}
          </Styles.CoverContainer>
        </Styles.Container>
      </Styles.TooltipCursor>
      {withColorVariants ? (
        <ColorVariants
          variants={colorVariants}
          activeVariantId={activeVariantId}
          colorVariantsNumber={colorVariantsNumber}
          isSelectActive={showSelect}
          isSmallDesktop={isSmallDesktop}
          onVariantClick={onVariantClick}
          onProductClick={onProductClick}
        />
      ) : null}
      <Box position="relative">
        {showAdvancedInfo && (
          <Box
            className="product-info-container"
            display="flex"
            flexDirection="column"
            justifyContent={centerAdvancedInfo ? 'center' : undefined}
            height={isCompactDescription ? 'auto' : '56px'}
            marginTop={pxToEm(8)}
            padding={`0 ${pxToEm(4)}`}
          >
            {!shouldShowHoverAdvancedInfo ? (
              <CardInfo
                title={title}
                subtitle1={subtitle1}
                subtitle2={subtitle2}
                categoryName={categoryName}
                brandName={brandName}
                childrenNumber={childrenNumber}
                isCompactDescription={isCompactDescription}
              />
            ) : null}

            {shouldShowHoverAdvancedInfo ? (
              <AdvancedInfoHover
                entityId={entityId}
                isOvernight={isOvernight}
                isDigital={isDigital}
                inStockLabel={inStockLabel}
                outOfStockLabel={outOfStockLabel}
                showOnHoverOnlyOvernight={showOnHoverOnlyOvernight}
                projects={projects}
                selectedProject={selectedProject!}
                projectPreLabel={projectPreLabel}
                createProjectButtonLabel={createProjectButtonLabel}
                searchPlaceholderLabel={searchPlaceholderLabel}
                onProjectClick={handleProjectClick}
                onCreateProject={onCreateProject}
              />
            ) : null}
          </Box>
        )}
      </Box>
      {showAdvancedInfo && showBag && (
        <AdvancedInfo
          entityId={entityId}
          actionType={actionType}
          productUrl={productUrl}
          isHover={isHover}
          isAddToCartLoading={isAddToCartLoading}
          isAddedToCart={isAddedToCart}
          disabled={disabled}
          hideActionButton={!!hideActionButton}
          showSelect={showSelect}
          actionButtonLabel={actionButtonLabel}
          addToCartHoverLabel={addToCartHoverLabel}
          addToCartTooltipLabel={addToCartTooltipLabel}
          isCustomAction={isCustomAction}
          onActionButtonClick={onActionButtonClick}
        />
      )}
    </Styles.RelativeContainer>
  );
};

export default ProductCard;
